import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { AutoResizer } from 'react-base-table';
import { metricKeys } from '@pulse/metrics';
import { Retry } from '@components/common';
import { humanize } from '@utils';
import { CURRENT_PRODUCT_MODE } from '@src/utils/constants';
import { chartDataSelector } from '@store/overviewDashboard/kpiData/selectors';
import { kpiLoadingSelector, kpiDataSelector } from '@store/overviewDashboard/benchmark/selectors';
import { setSelectedKPI } from '@store/overviewDashboard/base/actions';
import {
  selectedKPIConfigSelector,
  kpiMetricSelector,
} from '@store/overviewDashboard/base/selectors';
import { HelpPopup } from '@components/overview-dashboard/common/help-popup';
import { getMetricOvbDisplayName, getMetricUnitByKey } from '@src/utils/metrics';
import { KPIOrder } from './utils';
import { isKpiValid } from './Helper';
import {
  AdContentTableWrapper,
  StyledBaseTable,
  BoldTextWrap,
  StyledPara,
  StyledListWrapper,
  StyledListItem,
  ContentWrapper,
  LightTextWrap,
} from './styled';

const truncateNumber = (n, digits) => {
  return Math.trunc(n * 10 ** digits) / 10 ** digits;
};

const formatData = ({ column, rowData }) => {
  const { name } = rowData;
  const { dataKey } = column;
  const unit = getMetricUnitByKey({ metricKey: name });
  if (rowData[dataKey] !== undefined && typeof rowData[dataKey] == 'number') {
    return (
      <span>
        <BoldTextWrap>{humanize(truncateNumber(rowData[dataKey], 2), unit)}</BoldTextWrap>
        {unit}
      </span>
    );
  } else {
    return <LightTextWrap>N/A</LightTextWrap>;
  }
};

const columns: any = [
  {
    title: '',
    key: 'displayName',
    dataKey: 'displayName',
    width: 0,
    align: 'left',
    flexGrow: 1,
  },
  {
    title: 'Your Performance',
    key: 'value',
    dataKey: 'value',
    dataGetter: formatData,
    width: 170,
    align: 'center',
  },
  {
    title: 'Benchmark',
    key: 'benchmark',
    dataKey: 'benchmark',
    dataGetter: formatData,
    width: 170,
    align: 'center',
  },
];

const renderNoDataText = () => {
  return (
    <ContentWrapper>
      <StyledPara>
        Please select SPI: Conviva Good/Best and a Country to get started with Benchmark.
      </StyledPara>
      <StyledPara>
        <BoldTextWrap>Basic Benchmark: </BoldTextWrap> supports 3 dimensions: Country, Device Name,
        Content Category
      </StyledPara>
      <StyledPara>
        <BoldTextWrap>Advanced Benchmark: </BoldTextWrap> supports 13 dimensions:
      </StyledPara>
      <StyledListWrapper>
        <StyledListItem>
          <BoldTextWrap>Geo Related: </BoldTextWrap>Country, State, DMA (US), City
        </StyledListItem>
        <StyledListItem>
          <BoldTextWrap>Device Related: </BoldTextWrap> Device Name, Device Hardware Type, Device
          Marketing Name, Device Operating System, Device Operating System Family, Device Operating
          System Version, Browser Name, Browser Versions
        </StyledListItem>
        <StyledListItem>
          <BoldTextWrap>Content: </BoldTextWrap>Content Category
        </StyledListItem>
        <StyledListItem>
          <BoldTextWrap>Connectivity: </BoldTextWrap>ASN, ISP, Connection Type
        </StyledListItem>
      </StyledListWrapper>
      <br></br>
      <StyledPara>
        If you don’t have <BoldTextWrap>Advanced Benchmark</BoldTextWrap> enabled, please reach out
        to your account team.
      </StyledPara>
    </ContentWrapper>
  );
};

function Benchmark({ helpContext, retryHandler }) {
  const benchmarkStore = useSelector(kpiDataSelector);
  const selectedKPIConfig = useSelector(selectedKPIConfigSelector);
  const selectedMetric = useSelector(kpiMetricSelector);
  const dispatch = useDispatch();

  const benchmarkData = get(benchmarkStore, ['response', 'benchmarkData', 'result', 0], {});

  const metricData = useSelector(chartDataSelector);
  const { loading } = useSelector(kpiLoadingSelector);
  const { METRIC_ARR } = KPIOrder(metricData);
  const data = METRIC_ARR.map((metricInfo) => {
    const bm = benchmarkData ? benchmarkData[metricInfo.name] : undefined;

    metricInfo.benchmark = bm;
    metricInfo.displayName = getMetricOvbDisplayName({ metricKey: metricInfo.name });
    return metricInfo;
  });

  const renderHelpText = () => {
    const data: any = [];
    data.push(
      <>
        <ContentWrapper>
          <StyledPara>
            Conviva Benchmark is defined as the 95th Percentile. For example, if there are 100 video
            publishers with data in a specific cohort (such as United States, Live, Roku), the
            Benchmark is the 5th.
          </StyledPara>
          <StyledPara>
            In cases where Conviva is not measuring enough traffic to establish a meaningful
            benchmark value for a specific cohort, this will not be available.
          </StyledPara>
        </ContentWrapper>
      </>,
    );
    return data;
  };
  const onRowClick = (row) => {
    const clickedMetric = row.rowData.name;
    if (selectedMetric === clickedMetric) {
      dispatch(setSelectedKPI(metricKeys[CURRENT_PRODUCT_MODE].StreamingPerformanceIndex));
    } else {
      dispatch(setSelectedKPI(clickedMetric));
    }
  };
  const rowEventHandlers = {
    onClick: onRowClick,
  };
  const _rowClassName = ({ rowData }) => {
    if (rowData.name === selectedMetric) {
      return 'selected';
    }
    return 'not-selected';
  };
  const isKpiFound = isKpiValid(selectedKPIConfig);
  const renderBenchmarkTable = () => {
    if (isKpiFound && !isEmpty(data) && !isEmpty(benchmarkData)) {
      return (
        <AutoResizer>
          {({ width, height }) => (
            <StyledBaseTable
              data={data}
              columns={columns}
              headerHeight={30}
              rowHeight={45}
              minHeight={330}
              fixed={false}
              width={width}
              height={height}
              rowClassName={_rowClassName}
              rowEventHandlers={rowEventHandlers}
            />
          )}
        </AutoResizer>
      );
    }
    return <div>{renderNoDataText()}</div>;
  };
  const renderPopup = () => {
    const data = renderHelpText();
    return <HelpPopup title="Benchmark" helpText={data} helpContext={helpContext} />;
  };

  return (
    <AdContentTableWrapper>
      {renderPopup()}
      <Retry
        isLoading={loading}
        mountChildAlways={false}
        // uncomment to enable Retry
        // showError={showError}
        fetchData={retryHandler}
      >
        {renderBenchmarkTable()}
      </Retry>
    </AdContentTableWrapper>
  );
}

export default Benchmark;
